import React, { useState } from "react";

// ── Nav Configuration ────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { id: "overview", label: "Architecture & Overview", color: "#5F5E5A", section: "Overview" },
  
  // Dedicated Verification APIs
  { id: "aadhaar_send_otp", label: "Aadhaar: Send OTP", color: "#185FA5", section: "Dedicated Verification APIs" },
  { id: "aadhaar_verify_otp", label: "Aadhaar: Verify OTP", color: "#3B6D11", section: null },
  { id: "pan_verify", label: "PAN Card Check", color: "#185FA5", section: null },
  { id: "bank_account", label: "Bank Account Penny Drop", color: "#3B6D11", section: null },
  { id: "face_match", label: "Face Match 1:1", color: "#534AB7", section: null },
  { id: "face_liveness", label: "Face Liveness Anti-Spoof", color: "#534AB7", section: null },
  { id: "gstin_verify", label: "GSTIN Verification", color: "#185FA5", section: null },
  { id: "aadhaar_to_pan", label: "Aadhaar → Linked PAN", color: "#BA7517", section: null },
  
  // Unified KYC Gateway
  { id: "unified_gateway", label: "Unified KYC Gateway", color: "#D85A30", section: "Unified Gateway" },
];

const styles = {
  methodBadge: (method) => ({
    display: "inline-block", padding: "3px 9px", borderRadius: 6,
    fontSize: 11, fontWeight: 600, fontFamily: "monospace",
    background: method === "POST" ? "#EAF3DE" : "#E6F1FB",
    color: method === "POST" ? "#3B6D11" : "#0C447C",
  }),
  badge: (type) => ({
    display: "inline-block", padding: "1px 7px", borderRadius: 4,
    fontSize: 10, fontWeight: 600,
    background: type === "required" ? "#FCEBEB" : "#F1EFE8",
    color: type === "required" ? "#A32D2D" : "#5F5E5A",
  }),
  codeBlock: {
    background: "#F8FAFC", border: "0.5px solid #e2e8f0", borderRadius: 8,
    padding: "14px 16px", fontFamily: "monospace", fontSize: 12,
    lineHeight: 1.8, overflowX: "auto", position: "relative", whiteSpace: "pre",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: {
    padding: "8px 10px", textAlign: "left", background: "#f8fafc",
    color: "#64748b", fontWeight: 600, fontSize: 11, borderBottom: "0.5px solid #e2e8f0",
  },
  td: { padding: "9px 10px", borderBottom: "0.5px solid #f1f5f9", verticalAlign: "top" },
  noteBox: (color = "#BA7517") => ({
    background: color === "#BA7517" ? "#FAEEDA" : color === "#185FA5" ? "#E6F1FB" : "#FCEBEB",
    borderLeft: `3px solid ${color}`, borderRadius: 6, padding: "10px 14px",
    fontSize: 12, color: "#374151", marginBottom: 16,
  }),
};

function SectionLabel({ children, style }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 600, letterSpacing: ".06em",
      textTransform: "uppercase", color: "#64748b", marginTop: 20, marginBottom: 8, ...style,
    }}>
      {children}
    </div>
  );
}

function CodeBlock({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div style={styles.codeBlock}>
      <button
        onClick={copy}
        style={{
          position: "absolute", top: 8, right: 10, background: copied ? "#EAF3DE" : "#f1f5f9",
          border: "0.5px solid #cbd5e1", borderRadius: 5, padding: "3px 9px", fontSize: 11,
          color: copied ? "#3B6D11" : "#475569", cursor: "pointer", fontWeight: 500,
        }}
      >
        {copied ? "Copied!" : "Copy"}
      </button>
      {code}
    </div>
  );
}

function ParamRow({ name, type, required, desc }) {
  return (
    <tr>
      <td style={styles.td}><span style={{ fontFamily: "monospace", fontSize: 12, color: "#185FA5" }}>{name}</span></td>
      <td style={styles.td}><span style={{ color: "#64748b" }}>{type}</span></td>
      <td style={styles.td}>
        <span style={styles.badge(required ? "required" : "optional")}>
          {required ? "required" : "optional"}
        </span>
      </td>
      <td style={{ ...styles.td, color: "#374151" }}>{desc}</td>
    </tr>
  );
}

function ParamTable({ rows }) {
  return (
    <div style={{ border: "0.5px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Parameter</th>
            <th style={styles.th}>Type</th>
            <th style={styles.th}>Required</th>
            <th style={styles.th}>Description</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => <ParamRow key={i} {...r} />)}
        </tbody>
      </table>
    </div>
  );
}

export default function VerificationAPI() {
  const [activePage, setActivePage] = useState("overview");
  const [jwtToken, setJwtToken] = useState("YOUR_JWT_ACCESS_TOKEN");

  return (
    <main style={{ minHeight: "100vh", background: "#f8fafc", padding: "20px 0" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 16px" }}>
        
        {/* Container Card */}
        <div style={{
          background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 12,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)", overflow: "hidden", display: "flex", flexDirection: "column",
        }}>

          {/* Top Bar Header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "16px 24px", borderBottom: "0.5px solid #e2e8f0", flexWrap: "wrap", gap: 12,
          }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
                <span>Identity Verification & Unified KYC API Documentation</span>
                <span style={{ fontSize: 10, background: "#E6F1FB", color: "#185FA5", padding: "2px 7px", borderRadius: 4, fontWeight: 600 }}>
                  v2.0 Spec
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                Technical reference for Aadhaar, PAN, Bank Penny Drop, Biometric Face Match, and Unified Gateway.
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>JWT Token:</span>
              <input
                type="text"
                placeholder="Paste JWT token..."
                value={jwtToken}
                onChange={(e) => setJwtToken(e.target.value || "YOUR_JWT_ACCESS_TOKEN")}
                style={{
                  fontFamily: "monospace", fontSize: 12, border: "0.5px solid #cbd5e1",
                  borderRadius: 6, padding: "5px 10px", width: 220, background: "#f8fafc", color: "#0f172a",
                }}
              />
            </div>
          </div>

          {/* 2-Column Sidebar & Content Body */}
          <div style={{ display: "flex", flex: 1, minHeight: 600 }}>

            {/* Left Sidebar Menu */}
            <div style={{
              width: 260, flexShrink: 0, background: "#fff", borderRight: "0.5px solid #e2e8f0",
              padding: "16px 0", height: "100%", overflowY: "auto",
            }}>
              {NAV_ITEMS.map((item) => (
                <React.Fragment key={item.id}>
                  {item.section && (
                    <div style={{
                      padding: "12px 16px 4px", fontSize: 10, fontWeight: 600, letterSpacing: ".08em",
                      textTransform: "uppercase", color: "#94a3b8",
                    }}>
                      {item.section}
                    </div>
                  )}
                  <div
                    onClick={() => setActivePage(item.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 9, padding: "9px 16px", cursor: "pointer",
                      borderLeft: `3px solid ${activePage === item.id ? item.color : "transparent"}`,
                      background: activePage === item.id ? `${item.color}10` : "transparent",
                      color: activePage === item.id ? item.color : "#64748b",
                      fontWeight: activePage === item.id ? 600 : 400, fontSize: 13, transition: "all .15s",
                    }}
                  >
                    <span style={{
                      width: 7, height: 7, borderRadius: "50%", background: item.color, flexShrink: 0,
                      opacity: activePage === item.id ? 1 : 0.4,
                    }} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.label}
                    </span>
                  </div>
                </React.Fragment>
              ))}
            </div>

            {/* Right Main Content Panel */}
            <div style={{ flex: 1, padding: "28px 32px", maxWidth: 880, overflowX: "auto" }}>

              {/* OVERVIEW */}
              {activePage === "overview" && (
                <div>
                  <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4, color: "#0f172a" }}>Architecture & Global Conventions</h2>
                  <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>
                    Global configuration, authentication headers, and deduction policies.
                  </p>

                  <div style={styles.noteBox("#185FA5")}>
                    <strong>Production Base URL:</strong> <code style={{ fontFamily: "monospace" }}>https://pro.mokshiq.in</code>
                    <br />
                    All API calls require header: <code style={{ fontFamily: "monospace" }}>Authorization: Bearer &lt;TOKEN&gt;</code>
                  </div>

                  <SectionLabel>Wallet Ledger & Deductions</SectionLabel>
                  <div style={{ border: "0.5px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Service</th>
                          <th style={styles.th}>Admin Cost</th>
                          <th style={styles.th}>Deduction Policy</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={styles.td}><code style={{ color: "#185FA5" }}>AADHAAR</code> OTP Verification</td>
                          <td style={styles.td}>₹4.50</td>
                          <td style={styles.td}>Debited upon Step 2 successful verification</td>
                        </tr>
                        <tr>
                          <td style={styles.td}><code style={{ color: "#185FA5" }}>PANCARD</code> (Cashfree Lite)</td>
                          <td style={styles.td}>₹4.00</td>
                          <td style={styles.td}>Real-time check deduction</td>
                        </tr>
                        <tr>
                          <td style={styles.td}><code style={{ color: "#185FA5" }}>BANK_ACCOUNT</code> (Penny Drop)</td>
                          <td style={styles.td}>₹5.00</td>
                          <td style={styles.td}>Debited upon NPCI network check</td>
                        </tr>
                        <tr>
                          <td style={styles.td}><code style={{ color: "#185FA5" }}>FACE_MATCH</code> (1:1 Similarity)</td>
                          <td style={styles.td}>₹5.00</td>
                          <td style={styles.td}>Debited per face comparison</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* AADHAAR SEND OTP */}
              {activePage === "aadhaar_send_otp" && (
                <div>
                  <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4, color: "#0f172a" }}>/verify_aadhar/ (Step 1: Send OTP)</h2>
                  <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>Dispatches an OTP to citizen's Aadhaar-linked mobile number.</p>

                  <SectionLabel style={{ marginTop: 0 }}>Request Parameters</SectionLabel>
                  <ParamTable rows={[
                    { name: "user_id", type: "integer", required: true, desc: "Account ID of calling user" },
                    { name: "aadhaar_number", type: "string", required: true, desc: "12-digit Aadhaar number" },
                    { name: "step", type: "string", required: true, desc: 'Fixed value: "send_otp"' },
                  ]} />

                  <SectionLabel>cURL Example</SectionLabel>
                  <CodeBlock code={`curl -X POST "https://pro.mokshiq.in/verify_aadhar/" \\
  -H "Authorization: Bearer ${jwtToken}" \\
  -H "Content-Type: multipart/form-data" \\
  -d "user_id=1420" \\
  -d "aadhaar_number=987654321098" \\
  -d "step=send_otp"`} />

                  <SectionLabel>Response (200 OK)</SectionLabel>
                  <CodeBlock code={`{\n  "status": true,\n  "message": "OTP sent to Aadhaar-linked mobile number.",\n  "reference_num": "4819204918",\n  "data": {\n    "ref_id": "PLAN_REF_89214710"\n  }\n}`} />
                </div>
              )}

              {/* AADHAAR VERIFY OTP */}
              {activePage === "aadhaar_verify_otp" && (
                <div>
                  <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4, color: "#0f172a" }}>/verify_aadhar/ (Step 2: Verify OTP)</h2>
                  <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>Validates OTP, fetches full demographics, and syncs address profile.</p>

                  <SectionLabel style={{ marginTop: 0 }}>Request Parameters</SectionLabel>
                  <ParamTable rows={[
                    { name: "user_id", type: "integer", required: true, desc: "Account ID of user" },
                    { name: "aadhaar_number", type: "string", required: true, desc: "12-digit Aadhaar number" },
                    { name: "step", type: "string", required: true, desc: 'Fixed value: "verify_otp"' },
                    { name: "otp", type: "string", required: true, desc: "6-digit OTP received via SMS" },
                    { name: "ref_id", type: "string", required: true, desc: "Reference ID from Step 1" },
                  ]} />

                  <SectionLabel>cURL Example</SectionLabel>
                  <CodeBlock code={`curl -X POST "https://pro.mokshiq.in/verify_aadhar/" \\
  -H "Authorization: Bearer ${jwtToken}" \\
  -H "Content-Type: multipart/form-data" \\
  -d "user_id=1420" \\
  -d "aadhaar_number=987654321098" \\
  -d "step=verify_otp" \\
  -d "otp=548192" \\
  -d "ref_id=PLAN_REF_89214710"`} />

                  <SectionLabel>Response (200 OK)</SectionLabel>
                  <CodeBlock code={`{\n  "status": true,\n  "message": "Aadhaar verification successful.",\n  "data": {\n    "name": "RAHUL KUMAR SHARMA",\n    "dob": "1994-05-12",\n    "gender": "M",\n    "address": {\n      "dist": "Bhubaneswar",\n      "state": "Odisha",\n      "Zip": "751001"\n    }\n  }\n}`} />
                </div>
              )}

              {/* PAN VERIFY */}
              {activePage === "pan_verify" && (
                <div>
                  <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4, color: "#0f172a" }}>/verify_pancard/</h2>
                  <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>Validates PAN card status with Income Tax Department via Cashfree.</p>

                  <SectionLabel style={{ marginTop: 0 }}>cURL Example</SectionLabel>
                  <CodeBlock code={`curl -X POST "https://pro.mokshiq.in/verify_pancard/" \\
  -H "Authorization: Bearer ${jwtToken}" \\
  -H "Content-Type: multipart/form-data" \\
  -d "user_id=1420" \\
  -d "pan_number=ABCDE1234F" \\
  -d "name=Rahul Kumar Sharma"`} />

                  <SectionLabel>Response (200 OK)</SectionLabel>
                  <CodeBlock code={`{\n  "status": true,\n  "message": "PAN verification successful.",\n  "data": {\n    "status": "VALID",\n    "name": "RAHUL KUMAR SHARMA",\n    "pan": "ABCDE1234F",\n    "type": "Individual"\n  }\n}`} />
                </div>
              )}

              {/* BANK ACCOUNT */}
              {activePage === "bank_account" && (
                <div>
                  <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4, color: "#0f172a" }}>/verify_bank_account</h2>
                  <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>Validates account number & IFSC code via NPCI network penny drop.</p>

                  <SectionLabel style={{ marginTop: 0 }}>cURL Example</SectionLabel>
                  <CodeBlock code={`curl -X POST "https://pro.mokshiq.in/verify_bank_account" \\
  -H "Authorization: Bearer ${jwtToken}" \\
  -H "Content-Type: multipart/form-data" \\
  -d "user_id=1420" \\
  -d "account_number=30918237192" \\
  -d "ifsc=SBIN0001234" \\
  -d "name=Rahul Kumar Sharma"`} />

                  <SectionLabel>Response (200 OK)</SectionLabel>
                  <CodeBlock code={`{\n  "status": true,\n  "message": "Bank account verification successful.",\n  "data": {\n    "account_status": "VALID",\n    "name_at_bank": "RAHUL KUMAR SHARMA",\n    "bank_name": "STATE BANK OF INDIA",\n    "name_match_result": "EXACT"\n  }\n}`} />
                </div>
              )}

              {/* UNIFIED GATEWAY */}
              {activePage === "unified_gateway" && (
                <div>
                  <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4, color: "#0f172a" }}>/kyc_verification_request</h2>
                  <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>Single Unified Router for 13+ Government Verification checks.</p>

                  <SectionLabel style={{ marginTop: 0 }}>cURL Example (`kyc_type: PANCARD`)</SectionLabel>
                  <CodeBlock code={`curl -X POST "https://pro.mokshiq.in/kyc_verification_request" \\
  -H "Authorization: Bearer ${jwtToken}" \\
  -H "Content-Type: application/json" \\
  -d '{\n    "user_id": 1420,\n    "pin": "1234",\n    "kyc_type": "PANCARD",\n    "pan_number": "ABCDE1234F",\n    "name": "Rahul Kumar Sharma"\n  }'`} />
                </div>
              )}

              {/* OTHER PAGES FALLBACK */}
              {!["overview", "aadhaar_send_otp", "aadhaar_verify_otp", "pan_verify", "bank_account", "unified_gateway"].includes(activePage) && (
                <div>
                  <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4, color: "#0f172a" }}>
                    {NAV_ITEMS.find(n => n.id === activePage)?.label}
                  </h2>
                  <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>Unified Gateway specification call.</p>

                  <SectionLabel style={{ marginTop: 0 }}>cURL Example</SectionLabel>
                  <CodeBlock code={`curl -X POST "https://pro.mokshiq.in/kyc_verification_request" \\
  -H "Authorization: Bearer ${jwtToken}" \\
  -H "Content-Type: application/json" \\
  -d '{\n    "user_id": 1420,\n    "pin": "1234",\n    "kyc_type": "${activePage.toUpperCase()}"\n  }'`} />
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
